/**
 * AuditListPage — List view for LPA audit definitions.
 *
 * Shows a filterable card list of audits for the current factory.
 * Each card displays name, status badge, owner avatar+name, location breadcrumb,
 * schedule count, and created date. Clicking a card navigates to the detail view.
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/core/LanguageContext';
import { useAudits } from '../hooks/useAudits';
import type { LpaAuditWithDetails, LpaAuditStatus } from '../types';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Badge } from '@/shared/ui/badge';
import { Skeleton } from '@/shared/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/ui/avatar';
import { Card, CardContent } from '@/shared/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/shared/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/ui/popover';
import {
  Plus,
  Search,
  Filter,
  ClipboardCheck,
  MapPin,
  CalendarClock,
  ChevronRight,
  HelpCircle,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Status badge config
// ---------------------------------------------------------------------------

const STATUS_CONFIG: Record<
  LpaAuditStatus,
  { label: string; variant: 'default' | 'secondary' | 'outline'; className: string }
> = {
  draft: {
    label: 'Draft',
    variant: 'outline',
    className: 'border-amber-300 text-amber-700 dark:border-amber-600 dark:text-amber-400',
  },
  active: {
    label: 'Active',
    variant: 'default',
    className: 'bg-emerald-500/15 text-emerald-700 border-emerald-300 dark:text-emerald-400 dark:border-emerald-600',
  },
  archived: {
    label: 'Archived',
    variant: 'secondary',
    className: 'text-muted-foreground',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string | null, email: string): string {
  if (name) {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  }
  return email.charAt(0).toUpperCase();
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface StatusBadgeProps {
  status: LpaAuditStatus;
  t: (key: string, fallback: string) => string;
}

function StatusBadge({ status, t }: StatusBadgeProps) {
  const config = STATUS_CONFIG[status];
  const translatedLabel = t(`mod_lpa.audits.status.${status}`, config.label);

  return (
    <Badge variant={config.variant} className={config.className}>
      {translatedLabel}
    </Badge>
  );
}

function LocationBreadcrumb({
  audit,
  t,
}: {
  audit: LpaAuditWithDetails;
  t: (key: string, fallback: string) => string;
}) {
  const parts: string[] = [];
  if (audit.area_name) parts.push(audit.area_name);
  if (audit.line_name) parts.push(audit.line_name);
  if (audit.station_name) parts.push(audit.station_name);

  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      <MapPin className="h-3 w-3 shrink-0" />
      <span className="truncate">
        {parts.length > 0
          ? parts.join(' > ')
          : t('mod_lpa.audits.entireFactory', 'Entire factory')}
      </span>
    </div>
  );
}

function AuditCard({
  audit,
  onClick,
  t,
}: {
  audit: LpaAuditWithDetails;
  onClick: () => void;
  t: (key: string, fallback: string) => string;
}) {
  return (
    <Card
      className="group cursor-pointer transition-all hover:shadow-md hover:border-primary/20"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Left: owner avatar */}
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage
                    src={audit.owner?.avatar_url ?? undefined}
                    alt={audit.owner?.full_name ?? audit.owner?.email ?? ''}
                  />
                  <AvatarFallback className="text-xs font-medium">
                    {audit.owner
                      ? getInitials(audit.owner.full_name, audit.owner.email)
                      : '?'}
                  </AvatarFallback>
                </Avatar>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="font-medium">
                  {audit.owner?.full_name ?? audit.owner?.email ?? t('mod_lpa.audits.unknownOwner', 'Unknown')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('mod_lpa.audits.owner', 'Owner')}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Center: audit info */}
          <div className="flex-1 min-w-0 space-y-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-sm truncate">
                {audit.name}
              </h3>
              <StatusBadge status={audit.status} t={t} />
            </div>

            {audit.description && (
              <p className="text-xs text-muted-foreground line-clamp-1">
                {audit.description}
              </p>
            )}

            <div className="flex items-center gap-4 pt-0.5">
              <LocationBreadcrumb audit={audit} t={t} />

              <TooltipProvider delayDuration={300}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <CalendarClock className="h-3 w-3 shrink-0" />
                      <span>{audit.schedule_count}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {t('mod_lpa.audits.scheduleCount', `${audit.schedule_count} schedule(s)`)}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>

              <span className="text-xs text-muted-foreground">
                {formatDate(audit.created_at)}
              </span>
            </div>
          </div>

          {/* Right: chevron */}
          <ChevronRight className="h-5 w-5 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors shrink-0 mt-1" />
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Empty state
// ---------------------------------------------------------------------------

function EmptyState({
  hasFilters,
  t,
}: {
  hasFilters: boolean;
  t: (key: string, fallback: string) => string;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <ClipboardCheck className="h-12 w-12 text-muted-foreground mb-4" />
      <h3 className="text-lg font-medium mb-1">
        {hasFilters
          ? t('mod_lpa.audits.noResults', 'No audits match your filters')
          : t('mod_lpa.audits.emptyTitle', 'No audits yet')}
      </h3>
      <p className="text-sm text-muted-foreground max-w-sm">
        {hasFilters
          ? t('mod_lpa.audits.noResultsHint', 'Try adjusting your search or status filter.')
          : t('mod_lpa.audits.emptyHint', 'Create your first audit definition to start planning layered process audits for this factory.')}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-[76px] w-full rounded-lg" />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type StatusFilter = 'all' | 'draft' | 'active' | 'archived';

interface AuditListPageProps {
  onCreateAudit: () => void;
}

export default function AuditListPage({ onCreateAudit }: AuditListPageProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { data: audits, isLoading } = useAudits();

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  // Compute counts
  const counts = useMemo(() => {
    const all = audits ?? [];
    return {
      total: all.length,
      draft: all.filter((a) => a.status === 'draft').length,
      active: all.filter((a) => a.status === 'active').length,
      archived: all.filter((a) => a.status === 'archived').length,
    };
  }, [audits]);

  // Filtered list
  const filteredAudits = useMemo(() => {
    if (!audits) return [];

    return audits.filter((a) => {
      // Status filter
      if (statusFilter !== 'all' && a.status !== statusFilter) return false;

      // Text search
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchesName = a.name.toLowerCase().includes(q);
        const matchesDesc = a.description?.toLowerCase().includes(q);
        const matchesOwner =
          a.owner?.full_name?.toLowerCase().includes(q) ||
          a.owner?.email.toLowerCase().includes(q);
        if (!matchesName && !matchesDesc && !matchesOwner) return false;
      }

      return true;
    });
  }, [audits, searchQuery, statusFilter]);

  const hasFilters = searchQuery.trim() !== '' || statusFilter !== 'all';

  const handleAuditClick = (audit: LpaAuditWithDetails) => {
    navigate(`/lpa/audits/${audit.id}`);
  };

  // Status filter label for the dropdown trigger
  const getStatusLabel = () => {
    switch (statusFilter) {
      case 'all':
        return t('mod_lpa.audits.filterAll', `All (${counts.total})`);
      case 'draft':
        return t('mod_lpa.audits.filterDraft', `Draft (${counts.draft})`);
      case 'active':
        return t('mod_lpa.audits.filterActive', `Active (${counts.active})`);
      case 'archived':
        return t('mod_lpa.audits.filterArchived', `Archived (${counts.archived})`);
    }
  };

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('mod_lpa.audits.searchPlaceholder', 'Search audits...')}
            className="pl-9"
          />
        </div>

        {/* Status filter */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="min-w-[140px] justify-between gap-2">
              <Filter className="h-4 w-4" />
              {getStatusLabel()}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuRadioGroup
              value={statusFilter}
              onValueChange={(v) => setStatusFilter(v as StatusFilter)}
            >
              <DropdownMenuRadioItem value="all">
                {t('mod_lpa.audits.filterAll', `All (${counts.total})`)}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="draft">
                {t('mod_lpa.audits.filterDraft', `Draft (${counts.draft})`)}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="active">
                {t('mod_lpa.audits.filterActive', `Active (${counts.active})`)}
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="archived">
                {t('mod_lpa.audits.filterArchived', `Archived (${counts.archived})`)}
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Create button */}
        <Button onClick={onCreateAudit} className="gap-2">
          <Plus className="h-4 w-4" />
          {t('mod_lpa.audits.createButton', 'New Audit')}
        </Button>

        {/* Help popover */}
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="shrink-0">
              <HelpCircle className="h-4 w-4 text-muted-foreground" />
            </Button>
          </PopoverTrigger>
          <PopoverContent side="bottom" align="end" className="w-80 text-sm">
            <p className="font-medium mb-1">
              {t('mod_lpa.audits.infoTitle', 'Audit Definitions')}
            </p>
            <p className="text-muted-foreground">
              {t(
                'mod_lpa.audits.infoDescription',
                'Each audit defines what gets audited and where. Add schedules to an audit to define who audits, how often, and which questions. Draft audits are for planning — activate them when ready.',
              )}
            </p>
          </PopoverContent>
        </Popover>
      </div>

      {/* Audit list */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : filteredAudits.length === 0 ? (
        <EmptyState hasFilters={hasFilters} t={t} />
      ) : (
        <div className="space-y-2">
          {filteredAudits.map((audit) => (
            <AuditCard
              key={audit.id}
              audit={audit}
              onClick={() => handleAuditClick(audit)}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Result count */}
      {!isLoading && filteredAudits.length > 0 && (
        <p className="text-xs text-muted-foreground text-center">
          {t(
            'mod_lpa.audits.showing',
            `Showing ${filteredAudits.length} of ${counts.total} audits`,
          )}
        </p>
      )}
    </div>
  );
}
